import { useEffect, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Player } from "../../types";
import { useGameStore } from "../../store";
import {
  UNITS_PER_STEP,
  CAMERA_HEIGHT,
  CAMERA_DISTANCE,
  CAMERA_LERP_SPEED,
  CAMERA_LOOK_AHEAD,
  MOBILE_CAM_HEIGHT,
  MOBILE_CAM_DISTANCE_Z,
  MOBILE_CAM_BEHIND_X,
  MOBILE_CAM_LOOK_HEIGHT,
  MOBILE_CAM_LOOK_OFFSET_Y,
  MOBILE_CAM_LOOK_AHEAD,
  MOBILE_CAM_LERP_SPEED,
  LANE_SPACING,
  SPRING_STIFFNESS,
  SPRING_DAMPING,
  PODIUM_STAGE_X,
  PODIUM_CAM_HEIGHT,
  PODIUM_CAM_LOOK_HEIGHT,
  PODIUM_CAM_MIN_DISTANCE,
  PODIUM_CAM_WIDTH_MARGIN,
  PODIUM_CAM_FAR,
  DEFAULT_CAM_FAR,
} from "./constants";
import { PodiumView } from "./Podium";

// Durée pendant laquelle la caméra fait son orbite complète autour du joueur (en ms).
// Tient dans la fenêtre READY+SET de l'intro pour que l'orbite soit terminée à GO.
const INTRO_ORBIT_DURATION_MS = 4000;

interface SideCameraProps {
  players: Player[];
  currentPlayerId: string | undefined;
  isSpectator: boolean;
  gameCompleted: boolean;
  winnerId: string | null;
  winnerZ: number;
  // Phase podium : la caméra coupe sur le podium au lieu de zoomer sur le winner.
  onPodium?: boolean;
  // Point de vue fourni par le GLB, s'il en contient un.
  podiumView?: PodiumView | null;
  // Positions monde réellement occupées, pour le cadrage de repli.
  podiumSpots?: [number, number, number][];
}

// Caméra adaptative :
// - Spectateur : vue latérale large, suit la médiane des joueurs
// - Joueur mobile : vue 3/4 arrière rapprochée, suit son propre avatar
// - Fin de partie : zoom cinématique sur le winner
export function SideCamera({
  players,
  currentPlayerId,
  isSpectator,
  gameCompleted,
  winnerId,
  winnerZ,
  onPodium = false,
  podiumView = null,
  podiumSpots = [],
}: SideCameraProps) {
  const { camera } = useThree();
  const smoothPos = useRef(new THREE.Vector3(0, CAMERA_HEIGHT, CAMERA_DISTANCE));
  const smoothLookAt = useRef(new THREE.Vector3(0, 1, 0));
  // Spring-damper sur l'axe X — mêmes constantes que PlayerCharacter pour
  // que la caméra suive l'avatar avec exactement la même dynamique
  // (sinon la caméra "translate" devant l'avatar quand le joueur enchaîne les bonnes réponses).
  const trackedX = useRef(0);
  const trackedVelX = useRef(0);
  // Anchor temporel pour l'orbite caméra de l'intro
  const introStartedAt = useGameStore((s) => s.introStartedAt);
  // La coupe sur le podium est un CUT, pas un mouvement : elle se fait pendant le
  // flash blanc. Ce flag évite de re-snapper à chaque frame ensuite.
  const podiumSnapped = useRef(false);

  // Le diorama du podium est bien plus profond que la piste : sans relever le plan
  // de clipping, son décor de fond disparaît (cf. PODIUM_CAM_FAR).
  useEffect(() => {
    const cam = camera as THREE.PerspectiveCamera;
    if (!cam.isPerspectiveCamera) return;
    cam.far = onPodium ? PODIUM_CAM_FAR : DEFAULT_CAM_FAR;
    cam.updateProjectionMatrix();
  }, [onPodium, camera]);

  useFrame((_, delta) => {
    let targetPos: THREE.Vector3;
    let targetLookAt: THREE.Vector3;
    let lerpSpeed: number;

    if (onPodium) {
      if (podiumView) {
        // Point de vue auteur (caméra ou empty repère dans le GLB) : on le respecte
        // tel quel, c'est le cadrage voulu par qui a modélisé la scène.
        targetPos = podiumView.position.clone().add(new THREE.Vector3(PODIUM_STAGE_X, 0, 0));
        targetLookAt = podiumView.target.clone().add(new THREE.Vector3(PODIUM_STAGE_X, 0, 0));
      } else {
        // Repli : les places sont alignées sur X, on se recule sur +Z assez pour
        // contenir la largeur OCCUPÉE. Sur une partie à 2 joueurs, cadrer les 10
        // emplacements rendrait les avatars minuscules.
        const xs = podiumSpots.map((p) => p[0]);
        const zs = podiumSpots.map((p) => p[2]);
        const centerX = xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : PODIUM_STAGE_X;
        const frontZ = zs.length ? Math.max(...zs) : 0;
        const width = xs.length ? Math.max(...xs) - Math.min(...xs) : 0;
        // fov 50 vertical ; on prend une demi-largeur visible approchée et on
        // garde PODIUM_CAM_MIN_DISTANCE comme plancher pour les petites parties.
        const distance = Math.max(
          PODIUM_CAM_MIN_DISTANCE,
          (width * PODIUM_CAM_WIDTH_MARGIN) / 2 / Math.tan((50 * Math.PI) / 180 / 2)
        );
        targetPos = new THREE.Vector3(centerX, PODIUM_CAM_HEIGHT, frontZ + distance);
        targetLookAt = new THREE.Vector3(centerX, PODIUM_CAM_LOOK_HEIGHT, frontZ);
      }

      if (!podiumSnapped.current) {
        // Cut franc, masqué par le flash blanc
        podiumSnapped.current = true;
        smoothPos.current.copy(targetPos);
        smoothLookAt.current.copy(targetLookAt);
        camera.position.copy(smoothPos.current);
        camera.lookAt(smoothLookAt.current);
        return;
      }
      lerpSpeed = 0.06;
    } else if (gameCompleted && winnerId) {
      // Zoom cinématique sur le winner
      const winner = players.find((p) => p.id === winnerId);
      const wx = (winner?.position || 0) * UNITS_PER_STEP;
      targetPos = new THREE.Vector3(wx + 5, 3.5, winnerZ + 5);
      targetLookAt = new THREE.Vector3(wx, 1.2, winnerZ);
      lerpSpeed = 0.03;
    } else if (!isSpectator) {
      // Mode joueur mobile : caméra 3/4 arrière, proche du personnage
      const me = players.find((p) => p.id === currentPlayerId);
      const meIndex = players.findIndex((p) => p.id === currentPlayerId);
      const targetX = (me?.position || 0) * UNITS_PER_STEP;

      // Spring-damper identique à celui de l'avatar — garantit la synchro visuelle
      const displacement = trackedX.current - targetX;
      const springForce = -SPRING_STIFFNESS * displacement;
      const dampForce = -SPRING_DAMPING * trackedVelX.current;
      trackedVelX.current += (springForce + dampForce) * delta;
      trackedX.current += trackedVelX.current * delta;
      const myX = trackedX.current;

      const lanesOffset = ((players.length - 1) * LANE_SPACING) / 2;
      const myZ = meIndex >= 0 ? -meIndex * LANE_SPACING + lanesOffset : 0;

      if (introStartedAt) {
        // --- Intro : orbite horizontale de la caméra autour de l'avatar (l'avatar reste statique)
        const elapsed = Date.now() - introStartedAt;
        const progress = Math.min(1, elapsed / INTRO_ORBIT_DURATION_MS);
        const eased = 1 - Math.pow(1 - progress, 2); // ease-out quad => arrive doucement à la position de repos
        // Position de repos = (myX + BEHIND_X, _, myZ + DISTANCE_Z). On la paramétrise en (radius, baseAngle).
        const baseAngle = Math.atan2(MOBILE_CAM_DISTANCE_Z, MOBILE_CAM_BEHIND_X);
        const radius = Math.hypot(MOBILE_CAM_BEHIND_X, MOBILE_CAM_DISTANCE_Z);
        const angle = baseAngle + eased * Math.PI * 2;
        targetPos = new THREE.Vector3(
          myX + radius * Math.cos(angle),
          MOBILE_CAM_HEIGHT,
          myZ + radius * Math.sin(angle)
        );
        // LookAt fixé sur l'avatar (sans look-ahead pendant l'orbite)
        targetLookAt = new THREE.Vector3(
          myX,
          MOBILE_CAM_LOOK_HEIGHT + MOBILE_CAM_LOOK_OFFSET_Y,
          myZ
        );
        // Pendant l'orbite on snap directement (pas de lerp, l'easing fait déjà le smoothing)
        smoothPos.current.copy(targetPos);
        smoothLookAt.current.copy(targetLookAt);
        camera.position.copy(smoothPos.current);
        camera.lookAt(smoothLookAt.current);
        return;
      }

      targetPos = new THREE.Vector3(
        myX + MOBILE_CAM_BEHIND_X,
        MOBILE_CAM_HEIGHT,
        myZ + MOBILE_CAM_DISTANCE_Z
      );
      targetLookAt = new THREE.Vector3(
        myX + MOBILE_CAM_LOOK_AHEAD,
        MOBILE_CAM_LOOK_HEIGHT + MOBILE_CAM_LOOK_OFFSET_Y,
        myZ
      );
      lerpSpeed = MOBILE_CAM_LERP_SPEED;
    } else {
      // Mode spectateur : vue latérale large, ancrée sur le LEADER (joueur le plus avancé).
      // Le leader est cadré dans la moitié droite de l'écran, ce qui laisse de la place
      // aux followers à sa gauche — ils restent visibles tant qu'ils ne sont pas trop loin derrière.
      const SPECTATOR_LEADER_BACK_BIAS = 8;
      let focusX = 0;
      if (players.length > 0) {
        const leaderX = Math.max(
          ...players.map((p) => (p.position || 0) * UNITS_PER_STEP)
        );
        focusX = leaderX - SPECTATOR_LEADER_BACK_BIAS;
      }

      targetPos = new THREE.Vector3(
        focusX + CAMERA_LOOK_AHEAD,
        CAMERA_HEIGHT,
        CAMERA_DISTANCE
      );
      targetLookAt = new THREE.Vector3(focusX + CAMERA_LOOK_AHEAD, 1, 0);
      lerpSpeed = CAMERA_LERP_SPEED;
    }

    smoothPos.current.lerp(targetPos, lerpSpeed);
    smoothLookAt.current.lerp(targetLookAt, lerpSpeed);

    // En mode joueur mobile, l'axe X est déjà smoothé par le spring-damper :
    // on bypass le lerp final pour rester exactement en phase avec l'avatar.
    if (!isSpectator && !onPodium && !(gameCompleted && winnerId)) {
      smoothPos.current.x = targetPos.x;
      smoothLookAt.current.x = targetLookAt.x;
    }

    camera.position.copy(smoothPos.current);
    camera.lookAt(smoothLookAt.current);
  });

  return null;
}
